import { RequestResultValue } from "../../common/api/requestResult";
import { HttpService } from "../../common/api/httpService";
import JSONbig from "json-bigint";
import {
    UeActivationFunnelReport,
    UeCampaignFunnelTab as UeCampaignFunnelTabReport,
    UeCampaignMeta,
    UeCampaignMoney,
    UeCampaignOverview,
    UeCampaignPromo,
    UeCampaignQuality,
    UeCampaignRoles,
    UeCampaignTopUsers,
    UeCampaignsReport,
    UeDepositQualityReport,
    UeGmvByGameReport,
    UeLtvReport,
    UeMarketplaceRoles,
    UeNetFlowReport,
    UeOverviewReport,
    UeParetoReport,
    UePaymentTakeRateReport,
    UePeriodParams,
    UeRepeatRateReport,
    UeRetentionReport,
    UeSellerBuyerReport,
    UeTimeToValueReport,
} from "../../models/unitEconomics";

/**
 * storeAsString: большие ulong (SteamId) → string, без потери точности.
 * Не используем useNativeBigInt + fallback на JSON.parse: на double (rates) он падает
 * и JSON.parse округляет 76561199476887049 → …060.
 */
const JSONbigSafe = JSONbig({ storeAsString: true });

function appendOptional(params: URLSearchParams, key: string, value: string | number | undefined | null) {
    if (value === undefined || value === null || value === "") {
        return;
    }
    params.set(key, String(value));
}

function periodQuery(params: UePeriodParams): string {
    const q = new URLSearchParams();
    q.set("from", params.from.toISOString());
    q.set("to", params.to.toISOString());
    appendOptional(q, "gameId", params.gameId);
    appendOptional(q, "take", params.take);
    return q.toString();
}

async function getJson<T>(path: string): Promise<RequestResultValue<T>> {
    const response = await HttpService.getInstance().requestGetAsync(path);
    const text = await response.text();
    const body = JSONbigSafe.parse(text) as T;
    if (response.status >= 200 && response.status < 300) {
        return new RequestResultValue<T>(true, body);
    }
    return new RequestResultValue<T>(false);
}

const base = "admin/UnitEconomics";

export const ueOverviewApi = (p: UePeriodParams) =>
    getJson<UeOverviewReport>(`${base}/overview?${periodQuery(p)}`);

export const ueActivationFunnelApi = (p: UePeriodParams) =>
    getJson<UeActivationFunnelReport>(`${base}/activation-funnel?${periodQuery(p)}`);

export const ueTimeToValueApi = (p: UePeriodParams) =>
    getJson<UeTimeToValueReport>(`${base}/time-to-value?${periodQuery(p)}`);

export const ueRetentionApi = (p: UePeriodParams) =>
    getJson<UeRetentionReport>(`${base}/retention?${periodQuery(p)}`);

export const ueLtvApi = (p: UePeriodParams) =>
    getJson<UeLtvReport>(`${base}/ltv?${periodQuery(p)}`);

export const ueRepeatRateApi = (p: UePeriodParams) =>
    getJson<UeRepeatRateReport>(`${base}/repeat-rate?${periodQuery(p)}`);

export const ueParetoApi = (p: UePeriodParams) =>
    getJson<UeParetoReport>(`${base}/pareto?${periodQuery(p)}`);

export const uePaymentTakeRateApi = (p: UePeriodParams) =>
    getJson<UePaymentTakeRateReport>(`${base}/payment-take-rate?${periodQuery(p)}`);

export const ueNetFlowApi = (p: UePeriodParams) =>
    getJson<UeNetFlowReport>(`${base}/net-flow?${periodQuery(p)}`);

export const ueDepositQualityApi = (p: UePeriodParams) =>
    getJson<UeDepositQualityReport>(`${base}/deposit-quality?${periodQuery(p)}`);

export const ueGmvByGameApi = (p: UePeriodParams) =>
    getJson<UeGmvByGameReport>(`${base}/gmv-by-game?${periodQuery(p)}`);

export const ueSellerBuyerApi = (p: UePeriodParams) =>
    getJson<UeSellerBuyerReport>(`${base}/seller-buyer?${periodQuery(p)}`);

export const ueMarketplaceRolesApi = (p: UePeriodParams) =>
    getJson<UeMarketplaceRoles>(`${base}/marketplace-roles?${periodQuery(p)}`);

export const ueCampaignsApi = () => getJson<UeCampaignsReport>(`${base}/campaigns`);

export const ueCampaignMetaApi = (campaignId: number) =>
    getJson<UeCampaignMeta>(`${base}/campaigns/${campaignId}/meta`);

export const ueCampaignOverviewApi = (campaignId: number) =>
    getJson<UeCampaignOverview>(`${base}/campaigns/${campaignId}/overview`);

export const ueCampaignFunnelApi = (campaignId: number) =>
    getJson<UeCampaignFunnelTabReport>(`${base}/campaigns/${campaignId}/funnel`);

export const ueCampaignQualityApi = (campaignId: number) =>
    getJson<UeCampaignQuality>(`${base}/campaigns/${campaignId}/quality`);

export const ueCampaignRolesApi = (campaignId: number) =>
    getJson<UeCampaignRoles>(`${base}/campaigns/${campaignId}/roles`);

export const ueCampaignMoneyApi = (campaignId: number) =>
    getJson<UeCampaignMoney>(`${base}/campaigns/${campaignId}/money`);

/** Промокод кампании за срок кампании: воронка, применения по типам бонуса, динамика по дням. */
export const ueCampaignPromoApi = (campaignId: number) =>
    getJson<UeCampaignPromo>(`${base}/campaigns/${campaignId}/promo`);

export const ueCampaignTopUsersApi = (campaignId: number, take = 20) => {
    const q = new URLSearchParams();
    appendOptional(q, "take", take);
    const qs = q.toString();
    return getJson<UeCampaignTopUsers>(
        `${base}/campaigns/${campaignId}/top-users${qs ? `?${qs}` : ""}`
    );
};
